import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import useDerivedStateFromProps from '@elliemae/ds-utilities/hooks/useDerivedStateFromProps';
import { hashArray, toggleInObject, omit } from '@elliemae/ds-utilities/utils';
import { toggleSelectAll, checkAllRowsSelected, toggleSingleSelectable } from './helper.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function deriveSelectedStateFromPropsOnUpdate(rows) {
  return function () {
    var nextProp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var prevProp = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    if (nextProp.selectAll !== prevProp.selectAll) {
      var nextSelectedRows = nextProp.selectAll ? toggleSelectAll(rows, true) : hashArray(nextProp.selectedRows || []);
      return {
        selectedRows: nextSelectedRows,
        selectAll: checkAllRowsSelected(rows, Object.keys(nextSelectedRows))
      };
    }

    return {
      selectedRows: hashArray(nextProp.selectedRows || []),
      selectAll: checkAllRowsSelected(rows, Object.keys(nextProp.selectedRows))
    };
  };
}

function useSelectableState(grid) {
  var _grid$getInstance = grid.getInstance(),
      _grid$getInstance$pro = _grid$getInstance.props,
      _grid$getInstance$pro2 = _grid$getInstance$pro.rowKey,
      rowKey = _grid$getInstance$pro2 === void 0 ? 'id' : _grid$getInstance$pro2,
      selectedRows = _grid$getInstance$pro.selectedRows,
      selectAll = _grid$getInstance$pro.selectAll,
      selectable = _grid$getInstance$pro.selectable,
      onSelectRow = _grid$getInstance$pro.onSelectRow,
      onSelectAll = _grid$getInstance$pro.onSelectAll,
      rows = _grid$getInstance.rows,
      setLastSelectedRow = _grid$getInstance.setLastSelectedRow;

  var multiple = selectable === 'multi';

  var _useDerivedStateFromP = useDerivedStateFromProps({
    selectedRows: selectedRows,
    selectAll: selectAll
  }, {
    onUpdate: deriveSelectedStateFromPropsOnUpdate(rows)
  }),
      _useDerivedStateFromP2 = _slicedToArray(_useDerivedStateFromP, 2),
      selection = _useDerivedStateFromP2[0],
      setSelectedRowsState = _useDerivedStateFromP2[1]; // ACTIONS


  var handleSelectRow = function handleSelectRow(id, index) // eslint-disable-next-line max-params
  {
    var shift = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var lastSelectedRow = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    setSelectedRowsState(function (prevState) {
      var _grid$getInstance2 = grid.getInstance(),
          nextRows = _grid$getInstance2.rows;

      var wasSelected = Object.keys(prevState.selectedRows).some( // we need to match softly as the id may be a number
      // eslint-disable-next-line eqeqeq
      function (elem) {
        return elem == id;
      });
      var nextSelectedRows = multiple ? toggleInObject(prevState.selectedRows, id, true) : toggleSingleSelectable(prevState.selectedRows, id);
      setLastSelectedRow({
        index: index,
        mode: wasSelected ? 'deselect' : 'select'
      });

      if (shift && lastSelectedRow) {
        var prevSmallest = lastSelectedRow.index < index + 1;
        var sliceStart = prevSmallest ? lastSelectedRow.index : index;
        var sliceEnd = prevSmallest ? index + 1 : lastSelectedRow.index + 1;
        var rowSegment = nextRows.slice(sliceStart, sliceEnd).map(function (row) {
          return row[rowKey];
        });

        if (lastSelectedRow.mode === 'deselect') {
          nextSelectedRows = omit(prevState.selectedRows, rowSegment);
        } else {
          var selectedSegment = rowSegment.reduce(function (o, key) {
            return _objectSpread(_objectSpread({}, o), {}, _defineProperty({}, key, true));
          }, {});
          nextSelectedRows = _objectSpread(_objectSpread({}, prevState.selectedRows), selectedSegment);
        }
      }

      var nextState = {
        selectedRows: nextSelectedRows,
        selectAll: checkAllRowsSelected(nextRows, Object.keys(nextSelectedRows))
      };

      if (index !== null && index !== undefined) {
        onSelectRow(nextRows.filter(function (row) {
          return nextState.selectedRows[row[rowKey]];
        }), nextState.selectAll, index);
      }

      return nextState;
    });
  };

  var handleSelectAll = function handleSelectAll(checked) {
    if (!multiple) return;
    setLastSelectedRow(null);
    setSelectedRowsState(function () {
      var _grid$getInstance3 = grid.getInstance(),
          nextRows = _grid$getInstance3.composedRows;

      var nextState = {
        selectAll: checked,
        selectedRows: toggleSelectAll(nextRows, checked)
      };
      onSelectAll(nextState.selectAll);
      onSelectRow(checked ? nextRows : [], nextState.selectAll);
      return nextState;
    });
  };

  return {
    actions: {
      selectRow: handleSelectRow,
      selectAll: handleSelectAll
    },
    state: {
      selection: selection
    }
  };
}

export { useSelectableState };
//# sourceMappingURL=useSelectableState.js.map
